// src/features/dashboard/components/EmployeeCompliance/ComplianceLoginChart.tsx

import { useState } from "react";
import { Clock3 } from "lucide-react";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  LabelList,
} from "recharts";


// ----------------------
// Chart Data Interface
// ----------------------

interface LoginChartItem {
  day: string;
  loginTime: string;
  buffer: number;
}


// ----------------------
// Table Data Interface
// ----------------------

interface LoginTableItem {
  day: string;
  loginTime: string;
  window: string;
  status: string;
}


// ----------------------
// Component Props
// ----------------------

interface Props {
  data: LoginChartItem[];
  tableData: LoginTableItem[];
}


const BAR_COLOR = "#46C96E";


const ComplianceLoginChart = ({
  data,
  tableData,
}: Props) => {

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const allowedWindow =
  tableData.length > 0 ? tableData[0].window : "--:--–--:--";

  const values = data.map(x => x.buffer);

const minValue = values.length ? Math.min(...values) : 0;
const maxValue = values.length ? Math.max(...values) : 20;

// Add padding
const yMin = Math.max(
  0,
  Math.floor((minValue - 2) / 5) * 5
);

const yMax =
  Math.ceil((maxValue + 2) / 5) * 5;

// Generate ticks
const ticks: number[] = [];

for (let i = yMin; i <= yMax; i += 5) {
  ticks.push(i);
}

  return (
    <div className="rounded-2xl border border-[#D9DEE8] bg-white p-5">

      {/* Header */}

  <div className="mb-5 flex items-start gap-3">
  <div className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#15803D]">
    <Clock3
      size={11}
      color="#FFFFFF"
      strokeWidth={2.5}
    />
  </div>

  <div className="flex items-center gap-3">
    <h3 className="text-[15px] font-semibold text-[#002B5B] whitespace-nowrap">
      Gate 1 · Login Time
    </h3>

    <div className="rounded-full bg-[#EAF2FF] px-3 py-[5px] text-[12px] font-medium text-[#2563EB] whitespace-nowrap">
      Allowed window: {allowedWindow} · WTD history
    </div>
  </div>
</div>

      {/* Chart */}

      <div className="mt-2 h-[180px]">

        <ResponsiveContainer
          width="100%"
          height="100%"
        >

         <BarChart
  data={data}
  margin={{
    top: 10,
    right: 20,
    left: 30,   // Increase this
    bottom: 0,
  }}
>

            <CartesianGrid
              stroke="#EDF2F7"
              vertical={false}
            />

           <XAxis
  dataKey="day"
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 12,
    fill: "#334155",
  }}
  tickFormatter={(value: string) => {
    if (!value) return "";

    // If API returns "Monday 04"
    const parts = value.split(" ");

    if (parts.length >= 2) {
      return `${parts[0].substring(0, 3)} ${parts[1]}`;
    }

    // If API returns "Monday"
    return value.substring(0, 3);
  }}
/>

         <YAxis
  domain={[yMin, yMax]}
  ticks={ticks}
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 12,
    fill: "#334155",
  }}
  label={{
    value: "Buffer (mins)",
    angle: -90,
    position: "insideLeft",
    dx: -18,
    dy: 10,
    style: {
      fill: "#111827",
      fontSize: 12,
      fontWeight: 600,
      textAnchor: "middle",
    },
  }}
/>

            <Tooltip
  cursor={false}
  content={({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0].payload;

    return (
      <div className="rounded-lg border border-[#D0D5DD] bg-white px-4 py-3 shadow-xl">
        <div className="mb-2 text-[13px] font-semibold text-[#344054]">
          {label}
        </div>

        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#46C96E]" />

          <span className="text-[13px] text-[#344054]">
            Mins before window close:
          </span>

          <span className="text-[13px] font-semibold text-[#111827]">
            {item.loginTime}
          </span>

          <span className="text-[13px] font-semibold text-[#111827]">
            · {item.buffer}m before window ends
          </span>
        </div>
      </div>
    );
  }}
/>

           <Bar
  dataKey="buffer"
  radius={[4, 4, 0, 0]}
  barSize={36}
  onMouseMove={(_, index) => setHoveredIndex(index)}
  onMouseLeave={() => setHoveredIndex(null)}
>
  {data.map((_, index) => (
    <Cell
      key={index}
      fill={
        hoveredIndex === index
          ? "#16A34A"
          : "#46C96E"
      }
      style={{
        transition: "fill 180ms ease",
      }}
    />
  ))}

  <LabelList
    dataKey="loginTime"
    position="insideBottom"
    fill="#FFFFFF"
    style={{
      fontSize: 11,
      fontWeight: 700,
    }}
    dy={-18}
  />
</Bar>

          </BarChart>

        </ResponsiveContainer>

      </div>

      {/* Part 2: Table will come here */}
      {/* Login Table */}

<div className="mt-5 overflow-hidden rounded-lg border border-[#E5E7EB]">

  <table className="w-full border-collapse">

    <thead>

      <tr className="border-b border-[#E5E7EB] bg-white">

        <th className="px-6 py-3 text-left text-[13px] font-semibold text-[#111827]">
          Day
        </th>

        <th className="px-6 py-3 text-left text-[13px] font-semibold text-[#111827]">
          Login Time
        </th>

        <th className="px-6 py-3 text-left text-[13px] font-semibold text-[#111827]">
          Window
        </th>

        <th className="px-6 py-3 text-center text-[13px] font-semibold text-[#111827]">
          Status
        </th>

      </tr>

    </thead>

    <tbody>

      {tableData.map((row, index) => (

        <tr
          key={row.day}
          className={`
            border-b border-[#EEF2F7]
            ${index === tableData.length - 1 ? "bg-[#DCEAFE]" : "bg-white"}
          `}
        >

          <td className="px-6 py-[9px] text-[13px] text-[#111827]">
            {row.day}
          </td>

          <td className="px-6 py-[9px] text-[13px] text-[#111827]">
            {row.loginTime}
          </td>

          <td className="px-6 py-[9px] text-[13px] text-[#111827]">
            {row.window}
          </td>

          <td className="px-6 py-[9px] text-center">

            <span
              className="
                inline-flex
                items-center
                rounded-full
                bg-[#15803D]
                px-2
                py-[2px]
                text-[11px]
                font-semibold
                text-white
              "
            >
              {row.status}
            </span>

          </td>

        </tr>

      ))}

    </tbody>

  </table>

</div>

    </div>
  );
};

export default ComplianceLoginChart;